import React from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { createBatch, listBatches, updateBatch, type InventoryBatch } from '../api/inventory.ts';
import { useSearchParams } from 'react-router-dom';

const InventoryBatchesPage: React.FC = () => {
  const [params] = useSearchParams();
  const itemId = Number(params.get('itemId')||'0');
  const [rows, setRows] = React.useState<InventoryBatch[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<InventoryBatch | null>(null);

  const load = React.useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    try { setRows(await listBatches(itemId)); } finally { setLoading(false); }
  }, [itemId]);

  React.useEffect(() => { load(); }, [load]);

  const cols: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'batchCode', headerName: 'Batch', width: 140 },
    { field: 'manufactureDate', headerName: 'MFG', width: 120 },
    { field: 'expiryDate', headerName: 'EXP', width: 120 },
    { field: 'quantity', headerName: 'Qty', width: 90 },
    { field: 'unitCost', headerName: 'Unit Cost', width: 120 },
    { field: 'actions', headerName: '', width: 100, renderCell: (p) => (
      <IconButton onClick={() => { setEditing(p.row as InventoryBatch); setOpen(true); }}><EditIcon /></IconButton>
    ) }
  ];

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Batches for Item #{itemId}</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEditing(null); setOpen(true); }}>Add Batch</Button>
      </Box>
      <DataGrid autoHeight rows={rows} columns={cols} loading={loading} getRowId={(r)=> (r.id as any)} />

      <BatchDialog open={open} onClose={() => setOpen(false)} initial={editing} onSaved={load} itemId={itemId} />
    </Box>
  );
};

export default InventoryBatchesPage;

const BatchDialog: React.FC<{ open: boolean; onClose: () => void; initial: InventoryBatch | null; onSaved: () => void; itemId: number; }> = ({ open, onClose, initial, onSaved, itemId }) => {
  const [form, setForm] = React.useState<InventoryBatch>({ quantity: 0 });
  React.useEffect(() => { setForm(initial || { quantity: 0 }); }, [initial]);

  const save = async () => {
    if (form.id) await updateBatch(form.id, form); else await createBatch(itemId, form);
    onClose(); onSaved();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{form.id ? 'Edit Batch' : 'New Batch'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={6}><TextField fullWidth label="Batch Code" value={form.batchCode||''} onChange={e=> setForm({ ...form, batchCode:e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Manufacture Date" type="date" value={form.manufactureDate||''} onChange={e=> setForm({ ...form, manufactureDate:e.target.value })} InputLabelProps={{ shrink:true }}/></Grid>
          <Grid item xs={6}><TextField fullWidth label="Expiry Date" type="date" value={form.expiryDate||''} onChange={e=> setForm({ ...form, expiryDate:e.target.value })} InputLabelProps={{ shrink:true }}/></Grid>
          <Grid item xs={6}><TextField fullWidth label="Quantity" type="number" value={form.quantity??''} onChange={e=> setForm({ ...form, quantity:Number(e.target.value) })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Unit Cost" type="number" value={form.unitCost??''} onChange={e=> setForm({ ...form, unitCost:Number(e.target.value) })} /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Notes" value={form.notes||''} onChange={e=> setForm({ ...form, notes:e.target.value })} /></Grid>
        </Grid>
        <Box display="flex" gap={1} justifyContent="flex-end" mt={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};


